/** Pure projection of a Timeline (everything on the MV clock, integer ms) into lanes, grids and context. */
import type { HeatBin, SceneMv, Timeline, TimelineLine, TimelineSection, TimelineWord } from "../types";

export type Grids = { beat: number[]; downbeat: number[]; line: number[]; section: number[]; kick: number[];
  snare: number[] };
export type View = { v0: number; v1: number };
export type TextMode = "masked" | "raw";

/** Beat layers (exact integer ms on the MV clock), coarse to fine, then the drum hits. */
export const BEAT_LAYERS = ["bars", "beats", "eighths", "sixteenths", "kick", "snare", "hihat"] as const;
export type BeatLayer = (typeof BEAT_LAYERS)[number];
export const BEAT_LAYER_LABELS: Record<BeatLayer, string> = {
  bars: "Bars", beats: "Beats", eighths: "8ths", sixteenths: "16ths", kick: "Kick", snare: "Snare", hihat: "Hi-hat",
};

export const LANES = ["wave", "sections", "cuts", "lyrics", ...BEAT_LAYERS, "heat", "clips", "suggestions"] as const;
export type Lane = (typeof LANES)[number];
export const LANE_LABELS: Record<Lane, string> = {
  wave: "Waveform", sections: "Sections", cuts: "Cuts", lyrics: "Lyrics", ...BEAT_LAYER_LABELS, heat: "TikTok heat",
  clips: "Clips", suggestions: "Suggestions",
};
/** Lanes on by default: every lane but the dense 8th/16th grids (toggle them on; the beats table lists them too). */
export const DEFAULT_LANES: readonly Lane[] = LANES.filter((l) => l !== "eighths" && l !== "sixteenths");

export function isBeatLayer(l: string): l is BeatLayer {
  return (BEAT_LAYERS as readonly string[]).includes(l);
}

export function parseLanes(v: string | null): Set<Lane> {
  if (v == null) return new Set(DEFAULT_LANES);
  if (v.trim() === "all") return new Set(LANES);
  const want = new Set(v.split(",").map((s) => s.trim()));
  return new Set(LANES.filter((l) => want.has(l)));
}

export function lanesParam(on: Set<Lane>): string | null {
  const same = on.size === DEFAULT_LANES.length && DEFAULT_LANES.every((l) => on.has(l));
  if (same) return null;
  if (on.size === LANES.length) return "all";
  return LANES.filter((l) => on.has(l)).join(",") || "none";
}

/** A layer's times (integer ms, MV clock). Older timelines only carry beats/downbeats. */
export function layerTimes(tl: Timeline, layer: BeatLayer): number[] {
  const v = tl.beat_layers_mv_ms?.[layer];
  if (v) return v;
  if (layer === "beats") return tl.beats_mv_ms ?? [];
  if (layer === "bars") return tl.downbeats_mv_ms ?? [];
  return [];
}

/** bar.beat.sub of the i-th event of a layer (null when unknown). */
export function layerPos(tl: Timeline, layer: BeatLayer, i: number): string | null {
  return tl.beat_positions?.[layer]?.[i] ?? null;
}

export type BeatRow = { key: string; layer: BeatLayer; t: number; pos: string | null };

/** Text twin of the beat lanes: events of the chosen layers inside the view, by time (then coarse → fine). */
export function beatRows(tl: Timeline, view: View, layers: readonly BeatLayer[]): BeatRow[] {
  const out: BeatRow[] = [];
  for (const layer of layers) {
    layerTimes(tl, layer).forEach((t, i) => {
      if (t >= view.v0 && t < view.v1) out.push({ key: `${layer}-${t}`, layer, t, pos: layerPos(tl, layer, i) });
    });
  }
  const order = (l: BeatLayer) => BEAT_LAYERS.indexOf(l);
  return out.sort((a, b) => a.t - b.t || order(a.layer) - order(b.layer));
}

export function beatSourceText(tl: Timeline): string {
  if (tl.beat_source === "song-aligned") return "from the song's studio audio, projected through the alignment";
  if (tl.beat_source === "mv") return "from the MV's own audio (not aligned to its song yet)";
  return "no beat analysis yet";
}

const sortNum = (xs: Iterable<number>) => [...new Set(xs)].sort((a, b) => a - b);

export function durationOf(tl: Timeline): number {
  if (tl.duration_ms != null && tl.duration_ms > 0) return tl.duration_ms;
  const ends = [
    ...tl.segments.map((s) => s.mv0_ms + s.dur_ms), ...tl.cutscenes.map((c) => c.mv1_ms),
    ...tl.sections.map((s) => s.t1_mv_ms), ...layerTimes(tl, "beats"), 1000,
  ];
  return Math.max(...ends);
}

export function grids(tl: Timeline): Grids {
  return {
    beat: sortNum(layerTimes(tl, "beats")),
    downbeat: sortNum(layerTimes(tl, "bars")),
    kick: sortNum(layerTimes(tl, "kick")),
    snare: sortNum(layerTimes(tl, "snare")),
    line: sortNum(tl.lines.flatMap((l) => (l.t0_mv_ms == null ? [] : [l.t0_mv_ms]))),
    section: sortNum(tl.sections.map((s) => s.t0_mv_ms)),
  };
}

export function isChorus(label: string): boolean {
  return /refr|chorus|hook|kórus|korus/i.test(label);
}

export function sectionAt(sections: readonly TimelineSection[], t: number): TimelineSection | null {
  return sections.find((s) => s.t0_mv_ms <= t && t < s.t1_mv_ms) ?? null;
}

export function lineAt(lines: readonly TimelineLine[], t: number): TimelineLine | null {
  let best: TimelineLine | null = null;
  for (const l of lines) {
    if (l.t0_mv_ms == null || l.t1_mv_ms == null) continue;
    if (l.t0_mv_ms <= t && t < l.t1_mv_ms) best = l;
  }
  return best;
}

export function wordIndexAt(words: readonly TimelineWord[], t: number): number {
  for (let i = 0; i < words.length; i++) {
    const w = words[i] as TimelineWord;
    if (w.t0_mv_ms != null && w.t1_mv_ms != null && w.t0_mv_ms <= t && t < w.t1_mv_ms) return i;
  }
  return -1;
}

export function lineText(l: Pick<TimelineLine, "text_raw" | "text_masked">, mode: TextMode): string {
  return mode === "raw" ? l.text_raw : l.text_masked;
}

/** Lane viewport: the whole MV (zoomS = 0) or pages of zoomS seconds that flip when the playhead leaves them. */
export function viewFor(tMs: number, durationMs: number, zoomS: number): View {
  if (!zoomS || zoomS * 1000 >= durationMs) return { v0: 0, v1: Math.max(1, durationMs) };
  const span = zoomS * 1000;
  const page = Math.floor(Math.max(0, tMs) / span);
  const v0 = Math.min(page * span, Math.max(0, durationMs - span));
  return { v0, v1: v0 + span };
}

export function viewPage(tMs: number, zoomS: number): number {
  return zoomS ? Math.floor(Math.max(0, tMs) / (zoomS * 1000)) : 0;
}

export function pct(t: number, view: View): number {
  return ((t - view.v0) / (view.v1 - view.v0)) * 100;
}

export function timeAtFraction(f: number, view: View): number {
  return Math.round(view.v0 + Math.min(1, Math.max(0, f)) * (view.v1 - view.v0));
}

export type Box = { left: number; width: number };

/** Clip [t0, t1) to the view; null when outside. Width has a 0.15 % floor so tiny items stay clickable. */
export function box(t0: number, t1: number, view: View): Box | null {
  if (t1 <= view.v0 || t0 >= view.v1) return null;
  const a = Math.max(t0, view.v0);
  const b = Math.min(t1, view.v1);
  return { left: pct(a, view), width: Math.max(0.15, pct(b, view) - pct(a, view)) };
}

export type SectionBox = Box & { key: string; label: string; t0: number; t1: number; chorus: boolean;
  performer: string | null; idx: number };

export function projectSections(tl: Timeline, view: View): SectionBox[] {
  const out: SectionBox[] = [];
  tl.sections.forEach((s, idx) => {
    const b = box(s.t0_mv_ms, s.t1_mv_ms, view);
    if (b) out.push({ ...b, key: `s${idx}`, label: s.label, t0: s.t0_mv_ms, t1: s.t1_mv_ms, chorus: isChorus(s.label),
      performer: s.performer ?? null, idx });
  });
  return out;
}

export type CutsceneBox = Box & { key: string; kind: string; t0: number; t1: number };

export function projectCutscenes(tl: Timeline, view: View): CutsceneBox[] {
  const out: CutsceneBox[] = [];
  tl.cutscenes.forEach((c, i) => {
    const b = box(c.mv0_ms, c.mv1_ms, view);
    if (b) out.push({ ...b, key: `c${i}`, kind: c.kind, t0: c.mv0_ms, t1: c.mv1_ms });
  });
  return out;
}

export type LineBox = Box & { key: string; idx: number; text: string; t0: number; t1: number;
  performer: string | null; section: string | null };

export function projectLines(tl: Timeline, view: View, mode: TextMode): LineBox[] {
  const out: LineBox[] = [];
  for (const l of tl.lines) {
    if (l.t0_mv_ms == null || l.t1_mv_ms == null) continue;
    const b = box(l.t0_mv_ms, l.t1_mv_ms, view);
    if (b) out.push({ ...b, key: `l${l.idx}`, idx: l.idx, text: lineText(l, mode), t0: l.t0_mv_ms, t1: l.t1_mv_ms,
      performer: l.performer ?? null, section: l.section ?? null });
  }
  return out;
}

export type WordBox = Box & { key: string; i: number; text: string; flag: string; lineIdx: number };

/** Words are only drawn when the view is short enough to read them (≤ 30 s). */
export function projectWords(tl: Timeline, view: View, mode: TextMode): WordBox[] {
  if (view.v1 - view.v0 > 30_000) return [];
  const out: WordBox[] = [];
  tl.words.forEach((w, i) => {
    if (w.t0_mv_ms == null || w.t1_mv_ms == null) return;
    const b = box(w.t0_mv_ms, w.t1_mv_ms, view);
    if (b) out.push({ ...b, key: `w${i}`, i, text: mode === "raw" ? w.w_raw : w.w_masked, flag: w.flag,
      lineIdx: w.line_idx });
  });
  return out;
}

export function projectTicks(ms: readonly number[], view: View): { t: number; left: number; i: number }[] {
  const out: { t: number; left: number; i: number }[] = [];
  ms.forEach((t, i) => {
    if (t >= view.v0 && t < view.v1) out.push({ t, left: pct(t, view), i });
  });
  return out;
}

export type HeatBox = Box & { key: string; t0: number; t1: number; wPlay: number; wCount: number; intensity: number };

/** Heat bins aggregated into at most `maxBuckets` buckets across the view; intensity is relative to the max. */
export function projectHeat(tl: Timeline, view: View, maxBuckets = 200): HeatBox[] {
  const bin = tl.heat_bin_ms ?? 100;
  const span = view.v1 - view.v0;
  const bucket = Math.max(bin, Math.ceil(span / maxBuckets / bin) * bin);
  const acc = new Map<number, { p: number; c: number }>();
  for (const h of tl.heat) {
    if (h.t_mv_ms + bin <= view.v0 || h.t_mv_ms >= view.v1) continue;
    const k = Math.floor((h.t_mv_ms - view.v0) / bucket);
    const a = acc.get(k) ?? { p: 0, c: 0 };
    a.p += h.w_play;
    a.c += h.w_count;
    acc.set(k, a);
  }
  const max = Math.max(1e-12, ...[...acc.values()].map((a) => a.p));
  const out: HeatBox[] = [];
  for (const [k, a] of [...acc.entries()].sort((x, y) => x[0] - y[0])) {
    const t0 = view.v0 + k * bucket;
    const b = box(t0, t0 + bucket, view);
    if (b && a.p > 0) out.push({ ...b, key: `h${k}`, t0, t1: t0 + bucket, wPlay: a.p, wCount: a.c,
      intensity: a.p / max });
  }
  return out;
}

export type HeatRow = { t0: number; t1: number; play: number; count: number };

/** Text-table twin of the heatmap: shares per `rowMs` window (only windows with any usage). */
export function heatTable(heat: readonly HeatBin[], rowMs = 5000): HeatRow[] {
  const totP = heat.reduce((s, h) => s + h.w_play, 0) || 1;
  const totC = heat.reduce((s, h) => s + h.w_count, 0) || 1;
  const acc = new Map<number, HeatRow>();
  for (const h of heat) {
    const k = Math.floor(h.t_mv_ms / rowMs);
    const r = acc.get(k) ?? { t0: k * rowMs, t1: (k + 1) * rowMs, play: 0, count: 0 };
    r.play += h.w_play / totP;
    r.count += h.w_count / totC;
    acc.set(k, r);
  }
  return [...acc.values()].filter((r) => r.play > 0 || r.count > 0).sort((a, b) => a.t0 - b.t0);
}

/* ---------------------------------------------------------------- scenes (picture cuts) */

export const SCENE_KIND_LABELS: Record<string, string> = { cut: "Cut", dissolve: "Dissolve / fade" };

export function scenesOf(tl: Timeline): SceneMv[] {
  return tl.scenes ?? [];
}

export type SceneTick = { key: string; t: number; left: number; width: number | null; kind: string; score: number;
  shotLen: number | null; dur: number; i: number };

/** Scene transitions inside the view; a dissolve also spans its length (centred on t). */
export function projectScenes(tl: Timeline, view: View): SceneTick[] {
  const out: SceneTick[] = [];
  scenesOf(tl).forEach((s, i) => {
    if (s.t_mv_ms < view.v0 || s.t_mv_ms >= view.v1) return;
    const dur = s.dur_ms ?? 0;
    const b = dur > 0 ? box(s.t_mv_ms - dur / 2, s.t_mv_ms + dur / 2, view) : null;
    out.push({ key: `sc${i}`, t: s.t_mv_ms, left: b ? b.left : pct(s.t_mv_ms, view), width: b ? b.width : null,
      kind: s.kind, score: s.score, shotLen: s.shot_len_ms ?? null, dur, i });
  });
  return out;
}

export type ShotStats = { transitions: number; cuts: number; dissolves: number; shots: number; avgShotMs: number | null;
  perBar: number | null; bars: number };

/** Shot statistics of the MV window [t0, t1): transitions strictly inside, shots between them, per bar. */
export function shotStats(tl: Timeline, t0: number, t1: number): ShotStats {
  const inside = scenesOf(tl).filter((s) => s.t_mv_ms > t0 && s.t_mv_ms < t1);
  const edges = [t0, ...inside.map((s) => s.t_mv_ms), t1];
  const shots: number[] = [];
  for (let i = 1; i < edges.length; i++) {
    const d = (edges[i] as number) - (edges[i - 1] as number);
    if (d > 0) shots.push(d);
  }
  const bars = layerTimes(tl, "bars").filter((b) => b >= t0 && b < t1).length;
  return {
    transitions: inside.length, cuts: inside.filter((s) => s.kind === "cut").length,
    dissolves: inside.filter((s) => s.kind !== "cut").length, shots: shots.length,
    avgShotMs: shots.length ? Math.round(shots.reduce((a, b) => a + b, 0) / shots.length) : null,
    perBar: bars ? inside.length / bars : null, bars,
  };
}

export function shotStatsText(st: ShotStats): string {
  const avg = st.avgShotMs != null ? `avg shot ${(st.avgShotMs / 1000).toFixed(1)} s` : "no shots";
  const bar = st.perBar != null ? ` · ${st.perBar.toFixed(2)} cuts per bar` : "";
  return `${st.cuts} cut${st.cuts === 1 ? "" : "s"}${st.dissolves ? ` + ${st.dissolves} dissolve${
    st.dissolves === 1 ? "" : "s"}` : ""} · ${st.shots} shot${st.shots === 1 ? "" : "s"} · ${avg}${bar}`;
}

/* ---------------------------------------------------------------- In/Out from timed items */

/** The legacy cut rule (`content.cut_from_words`): a line's clip starts 120 ms before its first word and ends after
 * a 250 ms hold + 700 ms fade. */
export const LINE_LEAD_MS = 120;
export const LINE_TAIL_MS = 250 + 700;

export type Edges = { inMs: number; outMs: number; seekMs?: number };

export function lineEdges(t0: number, t1: number): Edges {
  return { inMs: Math.max(0, t0 - LINE_LEAD_MS), outMs: t1 + LINE_TAIL_MS, seekMs: t0 };
}

/** What a click on a timed item does: Shift = set In, Alt (Option) = set Out, otherwise seek. */
export function clickAction(e: { shiftKey: boolean; altKey: boolean }): "in" | "out" | "seek" {
  if (e.shiftKey) return "in";
  if (e.altKey) return "out";
  return "seek";
}

export function pctText(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}
